import React from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

/**
 * LoadMoreFooter - Footer component for expanding results
 * 
 * Shows "Load more" button and fast expand chips for quick expansion
 * 
 * Props:
 * - currentCount: Number of results currently loaded
 * - totalAvailable: Total results available (optional)
 * - onLoadMore: (increment) => void - Called with number to load
 * - fastExpand: Array of increment values (e.g., [50, 100, 1000])
 * - isLoading: Boolean - Loading state
 */
export default function LoadMoreFooter({
  currentCount = 25,
  totalAvailable = null,
  onLoadMore,
  fastExpand = [50, 100, 1000],
  isLoading = false,
  className = ''
}) {
  const remaining = totalAvailable ? totalAvailable - currentCount : null;
  
  const handleLoadMore = (increment = 25) => {
    onLoadMore?.(increment);
  };
  
  return (
    <div className={`py-6 px-4 border-t border-black/10 ${className}`}>
      <div className="flex flex-col items-center gap-3">
        {/* Progress indicator */}
        {totalAvailable && (
          <div className="text-xs text-black/50">
            Showing {currentCount.toLocaleString()} of ~{totalAvailable.toLocaleString()}
            {remaining !== null && remaining > 0 && (
              <span className="text-black/40"> • {remaining.toLocaleString()} remaining</span>
            )}
          </div>
        )}
        
        {/* Load more button */}
        <button
          onClick={() => handleLoadMore(25)}
          disabled={isLoading || (remaining !== null && remaining <= 0)}
          className={`
            flex items-center gap-2 px-6 py-2.5 rounded-lg
            font-medium text-sm transition-all
            ${isLoading || (remaining !== null && remaining <= 0)
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white border-2 border-[#5a20ff] text-[#5a20ff] hover:bg-[#5a20ff]/5 active:scale-[0.99]'
            }
          `}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Load 25 more
            </>
          )}
        </button>
        
        {/* Fast expand chips */}
        {!isLoading && remaining !== null && remaining > 0 && (
          <div className="flex items-center gap-2 flex-wrap justify-center">
            <span className="text-[10px] text-black/40">Quick expand:</span>
            {fastExpand.map(increment => {
              const newTotal = currentCount + increment;
              const isAvailable = !totalAvailable || newTotal <= totalAvailable;
              return (
                <button
                  key={increment}
                  onClick={() => handleLoadMore(increment)}
                  disabled={!isAvailable}
                  className={`
                    px-3 py-1 rounded-full text-[10px] font-medium transition-all
                    ${isAvailable
                      ? 'bg-[#5a20ff]/10 text-[#5a20ff] hover:bg-[#5a20ff]/20'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }
                  `}
                >
                  +{increment}
                </button>
              );
            })}
            {totalAvailable && currentCount + 1000 <= totalAvailable && (
              <button
                onClick={() => handleLoadMore(1000)}
                className="px-3 py-1 rounded-full text-[10px] font-medium bg-[#52dcb3]/10 text-[#52dcb3] hover:bg-[#52dcb3]/20 transition-all"
              >
                All 1K
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
